import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Tabs,
  Tab,
  Autocomplete,
  Box,
  Avatar,
} from "@mui/material";
import { FixedSizeList } from "react-window";
import { IniInfo, PackageConfig } from "system/types/interfaces";

const LISTBOX_ITEM_SIZE = 40;
const LISTBOX_OVERSCAN = 5;
const LISTBOX_MAX_VISIBLE_ITEMS = 8;

const VirtualizedListbox = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function VirtualizedListbox(props, ref) {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);
    const itemCount = itemData.length;
    const height = Math.min(LISTBOX_MAX_VISIBLE_ITEMS, Math.max(itemCount, 1)) * LISTBOX_ITEM_SIZE;

    return (
      <div ref={ref} {...other}>
        <FixedSizeList
          height={height}
          width="100%"
          itemSize={LISTBOX_ITEM_SIZE}
          itemCount={itemCount}
          overscanCount={LISTBOX_OVERSCAN}
        >
          {({ index, style }) => <div style={style}>{itemData[index]}</div>}
        </FixedSizeList>
      </div>
    );
  }
);

interface AddPackageModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: IniInfo, type: "package" | "comment") => void;
  packages: PackageConfig[];
  globalCatalog?: PackageConfig[];
  initialComment?: string;
  initialEmptyLinesBefore?: number;
  mode: "add" | "edit";
}

const AddPackageModal: React.FC<AddPackageModalProps> = ({
  open,
  onClose,
  onSubmit,
  packages,
  globalCatalog = [],
  initialComment = "",
  initialEmptyLinesBefore = 0,
  mode,
}) => {
  const [tabIndex, setTabIndex] = useState<0 | 1>(0); // 0 = Package, 1 = Comment
  const [selectedPackage, setSelectedPackage] = useState<PackageConfig | null>(null);
  const [leadingComment, setLeadingComment] = useState<string>("");
  const autocompleteRef = useRef<HTMLInputElement | null>(null);
  const commentInputRef = useRef<HTMLInputElement | null>(null);

  const mergedOptions = React.useMemo<PackageConfig[]>(() => {
    const byKey = new Map<string, PackageConfig>();
    globalCatalog.forEach((p) => {
      if (p.value) byKey.set(p.value.toLowerCase(), p);
    });
    packages.forEach((p) => {
      if (p.value) byKey.set(p.value.toLowerCase(), p);
    });
    return Array.from(byKey.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [packages, globalCatalog]);

  const handleSubmit = () => {
    if (tabIndex === 0 && selectedPackage?.value.length === 0) return;
    if (tabIndex === 1 && leadingComment.length === 0) return;

    if (tabIndex === 0 && selectedPackage) {
      onSubmit(
        {
          value: "",
          name: selectedPackage.value,
          delimiter: "",
          inlineComment: leadingComment || " ",
          isComment: false,
          emptyLinesBefore: 0,
        },
        "package"
      );
    } else if (tabIndex === 1) {
      // Step 1: Extract leading newlines and calculate emptyLinesBefore
      const newlineMatch = leadingComment.match(/^(\n*)/);
      const leadingNewlines = newlineMatch ? newlineMatch[0].split("\n").length - 1 : 0;

      // Step 2: Remove leading newlines and trim the content
      const contentWithoutNewlines = leadingComment.replace(/^(\n*)/, "").trim();

      // Step 3: Ensure the trimmed content starts with a semicolon
      const finalComment = contentWithoutNewlines.startsWith(";") ? contentWithoutNewlines : `; ${contentWithoutNewlines}`;
      onSubmit(
        {
          name: `New-Comment-${Date.now()}`,
          value: finalComment || "",
          delimiter: "",
          inlineComment: null,
          emptyLinesBefore: leadingNewlines,
          isComment: true,
        },
        "comment"
      );
    }
    onClose();
  };

  useEffect(() => {
    if (mode === "edit") {
      const newlines = "\n".repeat(initialEmptyLinesBefore);
      const reconstructedComment = `${newlines}${initialComment || ""}`;

      setLeadingComment(reconstructedComment);
      setTabIndex(1);
    } else {
      setLeadingComment("");
    }
  }, [initialComment, initialEmptyLinesBefore, mode]);

  useEffect(() => {
    if (!open) {
      setSelectedPackage(null);
      setLeadingComment("");
      setTabIndex(0);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      if (tabIndex === 0 && autocompleteRef.current) {
        const input = autocompleteRef.current.querySelector("input");
        input?.focus();
      } else if (tabIndex === 1 && commentInputRef.current) {
        commentInputRef.current.focus();
      }
    }
  }, [tabIndex, open]);

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ backgroundColor: "#1b1a19", color: "#ffffff" }}>
          <Typography variant="inherit" fontWeight="bold" color="#ffffff">
            {tabIndex === 0 ? "Add Line" : "Add Row Comment"}
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: "#1b1a19",
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Tabs value={mode === "edit" ? 1 : tabIndex} onChange={(_, value) => mode === "add" && setTabIndex(value)} centered>
            <Tab label="Rule" disabled={mode === "edit"} />
            <Tab label="Row Comment" />
          </Tabs>

          {tabIndex === 0 && (
            <>
              <Autocomplete
                disableListWrap
                options={mergedOptions}
                getOptionLabel={(option) => option.value}
                value={selectedPackage}
                disabled={mergedOptions.length === 0}
                onChange={(_, newValue) => setSelectedPackage(newValue)}
                ListboxComponent={VirtualizedListbox}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoFocus
                    label={`${mergedOptions.length === 0 ? "Loading packages..." : "Select a package"}`}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: selectedPackage?.iconUrl ? (
                        <Avatar
                          src={selectedPackage.iconUrl}
                          alt={`${selectedPackage.value} Icon`}
                          sx={{ width: 24, height: 24, borderRadius: "0%", mr: 1 }}
                        />
                      ) : null,
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" sx={{ display: "flex", alignItems: "center", gap: 1 }} {...props} key={option.value}>
                    {option.iconUrl ? (
                      <Avatar src={option.iconUrl} alt={`${option.value} Icon`} sx={{ width: 24, height: 24, borderRadius: "0%" }} />
                    ) : (
                      <Avatar
                        src="/img/DefaultPackage.png"
                        alt="Package Icon"
                        sx={{
                          width: 24,
                          height: 24,
                          marginRight: "8px",
                          borderRadius: "0%",
                        }}
                      />
                    )}
                    <Typography noWrap>{option.value}</Typography>
                  </Box>
                )}
              />

              <TextField
                fullWidth
                label="Description"
                multiline
                value={selectedPackage?.description || ""}
                disabled
                InputProps={{
                  readOnly: true,
                  style: { color: "#ffffff" },
                }}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: "#ffffff",
                  },
                }}
              />

              <TextField
                fullWidth
                label="Inline Comment"
                multiline
                rows={3}
                placeholder="Add your comment here"
                value={leadingComment}
                inputProps={{ style: { color: "#4caf50" } }}
                onChange={(e) => setLeadingComment(e.target.value)}
              />
            </>
          )}

          {tabIndex === 1 && (
            <TextField
              fullWidth
              label="Add Comment"
              multiline
              rows={7}
              placeholder="Add your comment here"
              value={leadingComment}
              inputProps={{ style: { color: "#4caf50" } }}
              inputRef={commentInputRef}
              onChange={(e) => setLeadingComment(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#1b1a19" }}>
          <Button variant="outlined" onClick={onClose} sx={{ color: "#ffffff", borderColor: "#757575" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit}>
            Next
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddPackageModal;
